import React from "react";
import styled from "styled-components";
import { Link } from "@inertiajs/react";
import { apiPost } from "../api";
import { LoggedInUserContext } from "../providers/ProvideLoggedInUser";

// Reusable hover/focus overlay for content cards on the rebranded homepage.
// Sits over the bottom ~75% of its container with a dark gradient so the
// action buttons stay legible regardless of the card's background.
//
// The host card must set `position: relative` on itself — this component is
// absolutely positioned to the nearest positioned ancestor.
//
// Props:
//   resourceType      — like/bookmark target class (e.g. "Curriculum", "Course", "Talk")
//   resourceId        — UUID of that resource
//   detailLink        — href for the "Lihat Detail" action (same-tab navigation)
//   showLike          — render the like button (default true)
//   showDislike       — render the dislike button (default true)
//   showBookmark      — render the bookmark button (default true)
//   showDetail        — render the "Lihat Detail" button (default true when detailLink given)
//   align             — 'bottom' (default) or 'top' — which edge the gradient anchors to
//   initialLiked      — boolean; initial pressed-state for the like button (hydrated from server)
//   initialDisliked   — boolean; initial pressed-state for the dislike button
//   initialBookmarked — boolean; initial pressed-state for the bookmark button
//
// Unauthenticated users: action clicks bounce them to the login popup on `/`
// with a redirect_after_login so they land back on the homepage after auth.

const Overlay = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  ${({ $align }) => ($align === "top" ? "top: 0;" : "bottom: 0;")}
  height: 75%;
  border-radius: ${({ $radius }) => $radius || "12px"};
  background: ${({ $align }) =>
    $align === "top"
      ? "linear-gradient(180deg, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.45) 60%, rgba(0,0,0,0) 100%)"
      : "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.85) 100%)"};
  opacity: 0;
  pointer-events: none;
  display: flex;
  align-items: ${({ $align }) => ($align === "top" ? "flex-start" : "flex-end")};
  padding: 14px;
  transition: opacity 200ms ease;

  [data-card-host]:hover > &,
  [data-card-host]:focus-within > & {
    opacity: 1;
    pointer-events: auto;
  }
`;

const ActionRow = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-start;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1 1 auto;
`;

const IconButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.92);
  border: none;
  color: #1f2937;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 140ms ease, background 140ms ease, color 140ms ease, box-shadow 140ms ease;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);

  & svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  /* FA 5 <i> glyphs render via font, not SVG — size them via font-size
     instead. line-height: 1 prevents the glyph from shifting baseline
     inside the round 36px button. */
  & i {
    font-size: 16px;
    line-height: 1;
    display: block;
  }

  &:hover, &:focus-visible {
    transform: translateY(-2px);
    background: #fff;
    color: #d85907;
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
  }

  &:focus-visible {
    outline: 2px solid #d85907;
    outline-offset: 2px;
  }

  ${({ $active, $activeColor, $iconOnly }) =>
    $active &&
    ($iconOnly
      ? `
        color: ${$activeColor || "#d85907"};

        &:hover, &:focus-visible {
          background: #ffffff;
          color: ${$activeColor || "#d85907"};
        }
      `
      : `
        background: ${$activeColor || "#d85907"};
        color: #ffffff;

        &:hover, &:focus-visible {
          background: ${$activeColor || "#d85907"};
          color: #ffffff;
        }
      `)
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
`;

// Inertia Link so navigating into rebranded detail pages (Sertifikasi
// course/curriculum/lesson) is an SPA visit instead of a full reload.
// Falls back to a full navigation when the destination returns a
// non-Inertia response (e.g. legacy show pages while a toggle is OFF).
const DetailLink = styled(Link)`
  background: #d85907;
  color: #ffffff;
  text-decoration: none;
  padding: 9px 16px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 0 6px 14px rgba(216, 89, 7, 0.35);
  transition: background 140ms ease, transform 140ms ease, box-shadow 140ms ease;

  &:hover, &:focus-visible {
    background: #b84a04;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 8px 18px rgba(216, 89, 7, 0.45);
  }

  &:focus-visible {
    outline: 2px solid #ffedd5;
    outline-offset: 2px;
  }

  & svg { width: 14px; height: 14px; }
`;

// Font Awesome 5 icons via <i> elements — same icon set the legacy site
// uses on the homes_v2/talks/lessons/courses show pages (see
// app/views/homes_v2/lessons/show.html.erb:156 etc). FA 5 is already
// loaded globally on the Inertia layout (inertia.html.erb:25), so this
// adds zero new dependencies. We toggle between `far` (regular outline)
// and `fas` (solid fill) based on the `filled` prop — the same legacy
// pattern matches the user's mental model from the previous site.
//
// All three icons accept `filled`. The active tint colour is driven by
// IconButton's `$activeColor` + `$iconOnly` (for thumbs) or by the
// button-fill active style (for bookmark) — see ActionRow below.
const ThumbUpIcon = ({ filled }) => (
  <i className={`${filled ? 'fas' : 'far'} fa-thumbs-up`} aria-hidden="true" />
);

const ThumbDownIcon = ({ filled }) => (
  <i className={`${filled ? 'fas' : 'far'} fa-thumbs-down`} aria-hidden="true" />
);

const BookmarkIcon = ({ filled }) => (
  <i className={`${filled ? 'fas' : 'far'} fa-bookmark`} aria-hidden="true" />
);

const DetailArrowIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" />
    <path d="M13 5l7 7-7 7" />
  </svg>
);

// Same-origin login redirect that preserves the current URL so the user
// lands back on the homepage after signing in.
function redirectToLogin() {
  if (typeof window === "undefined") return;
  const back = encodeURIComponent(window.location.pathname + window.location.search + window.location.hash);
  window.location.href = `/login?redirect_after_login=${back}`;
}

// apiPost resolves with `{ statusCode }` regardless of HTTP status (it never
// throws on non-2xx). Treat anything outside the 2xx success range as a
// failure so the overlay can roll back the optimistic UI update.
function isOkStatus(code) {
  return code >= 200 && code < 300;
}

function CardActionsOverlay({
  resourceType,
  resourceId,
  detailLink,
  showLike = true,
  showDislike = true,
  showBookmark = true,
  showDetail = true,
  align = "bottom",
  radius,
  initialLiked = false,
  initialDisliked = false,
  initialBookmarked = false,
}) {
  const { signedIn, loading } = React.use(LoggedInUserContext);

  const [liked, setLiked] = React.useState(initialLiked);
  const [disliked, setDisliked] = React.useState(initialDisliked);
  const [bookmarked, setBookmarked] = React.useState(initialBookmarked);
  const [busy, setBusy] = React.useState(false);

  const toggleLike = React.useCallback(
    async (likesValue) => {
      if (loading) return;
      if (!signedIn) return redirectToLogin();
      if (busy) return;
      setBusy(true);
      const prevLiked = liked;
      const prevDisliked = disliked;
      // Optimistic update — flips the current side, unflips the other
      if (likesValue) {
        setLiked(!prevLiked);
        if (!prevLiked) setDisliked(false);
      } else {
        setDisliked(!prevDisliked);
        if (!prevDisliked) setLiked(false);
      }
      try {
        const { statusCode } = await apiPost("/user_likes/create_v2", {
          body: {
            user_likes: {
              resource_type: resourceType,
              resource_id: resourceId,
              likes: likesValue,
            },
          },
        });
        if (!isOkStatus(statusCode)) {
          if (typeof console !== "undefined") console.warn("[CardActionsOverlay] like non-2xx", statusCode);
          setLiked(prevLiked);
          setDisliked(prevDisliked);
        }
      } catch (err) {
        if (typeof console !== "undefined") console.warn("[CardActionsOverlay] like failed", err);
        setLiked(prevLiked);
        setDisliked(prevDisliked);
      } finally {
        setBusy(false);
      }
    },
    [loading, signedIn, busy, liked, disliked, resourceType, resourceId],
  );

  const toggleBookmark = React.useCallback(async () => {
    if (loading) return;
    if (!signedIn) return redirectToLogin();
    if (busy) return;
    setBusy(true);
    const prev = bookmarked;
    setBookmarked(!prev);
    try {
      const { statusCode } = await apiPost("/user_bookmarks/create_v2", {
        body: {
          user_bookmarks: {
            resource_type: resourceType,
            resource_id: resourceId,
          },
        },
      });
      if (!isOkStatus(statusCode)) {
        if (typeof console !== "undefined") console.warn("[CardActionsOverlay] bookmark non-2xx", statusCode);
        setBookmarked(prev);
      }
    } catch (err) {
      if (typeof console !== "undefined") console.warn("[CardActionsOverlay] bookmark failed", err);
      setBookmarked(prev);
    } finally {
      setBusy(false);
    }
  }, [loading, signedIn, busy, bookmarked, resourceType, resourceId]);

  const canAct = !!(resourceType && resourceId);
  const hasDetail = showDetail && !!detailLink;

  return (
    <Overlay $align={align} $radius={radius}>
      <ActionRow>
        {showLike && canAct && (
          <IconButton
            type="button"
            aria-label={liked ? "Batalkan suka" : "Suka"}
            aria-pressed={liked}
            disabled={busy}
            $active={liked}
            $activeColor="#1BEB4B" /* matches legacy `.thumb-up.actived` colour */
            $iconOnly
            onClick={() => toggleLike(true)}
          >
            <ThumbUpIcon filled={liked} />
          </IconButton>
        )}
        {showDislike && canAct && (
          <IconButton
            type="button"
            aria-label={disliked ? "Batalkan tidak suka" : "Tidak suka"}
            aria-pressed={disliked}
            disabled={busy}
            $active={disliked}
            $activeColor="#FF3447" /* matches legacy `.thumb-down.actived` colour */
            $iconOnly
            onClick={() => toggleLike(false)}
          >
            <ThumbDownIcon filled={disliked} />
          </IconButton>
        )}
        {showBookmark && canAct && (
          <IconButton
            type="button"
            aria-label={bookmarked ? "Hapus bookmark" : "Bookmark"}
            aria-pressed={bookmarked}
            disabled={busy}
            $active={bookmarked}
            onClick={toggleBookmark}
          >
            <BookmarkIcon filled={bookmarked} />
          </IconButton>
        )}
        <Spacer />
        {hasDetail && (
          <DetailLink href={detailLink} aria-label="Lihat halaman detail">
            <span>Lihat Detail</span>
            <DetailArrowIcon />
          </DetailLink>
        )}
      </ActionRow>
    </Overlay>
  );
}

export default React.memo(CardActionsOverlay);
